import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { GitCompareArrows } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { cn } from "@/lib/cn";
import type { CampaignContactScanKind, CampaignContactScanResult } from "@/types/campaign";

interface Props {
  open: boolean;
  /** Every scanned row — new, update, and unchanged — one block each. */
  results: CampaignContactScanResult[];
  /** True while the import call that follows the confirmation is in flight. */
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: (selectedRowNumbers: number[]) => void;
}

/** "—" for a value the contact does not have, so an empty cell is never ambiguous. */
const shown = (value: string | null) => (value?.trim() ? value : "—");

const KIND_COPY: Record<
  CampaignContactScanKind,
  { badge: string; hint: string; border: string; header: string; badgeClass: string }
> = {
  new: {
    badge: "New",
    hint: "Will create this contact",
    border: "border-emerald-200",
    header: "bg-emerald-50",
    badgeClass: "bg-emerald-100 text-emerald-800",
  },
  update: {
    badge: "Update",
    hint: "Will overwrite the stored contact",
    border: "border-amber-200",
    header: "bg-amber-50",
    badgeClass: "bg-amber-100 text-amber-800",
  },
  unchanged: {
    badge: "Unchanged",
    hint: "Matches the stored contact",
    border: "border-slate-200",
    header: "bg-slate-50",
    badgeClass: "bg-slate-100 text-slate-700",
  },
};

function summaryPhrase(counts: { new: number; update: number; unchanged: number }): string {
  const parts: string[] = [];
  if (counts.new > 0) parts.push(`${counts.new} new`);
  if (counts.update > 0) parts.push(`${counts.update} to update`);
  if (counts.unchanged > 0) parts.push(`${counts.unchanged} unchanged`);
  if (parts.length === 0) return "Review the uploaded rows before importing.";
  return `${parts.join(", ")}. Tick the rows to import — unticked rows are left out.`;
}

/**
 * Review of `POST /campaigns/contacts/scan`: every uploaded row, coloured by
 * whether it will create a contact, overwrite one, or leave one as-is.
 */
export function CampaignContactScanModal({
  open,
  results,
  submitting = false,
  onCancel,
  onConfirm,
}: Props) {
  const [selected, setSelected] = useState<number[]>([]);

  useEffect(() => {
    if (open) setSelected(results.map((r) => r.row_number));
  }, [open, results]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const allSelected = results.length > 0 && selected.length === results.length;
  const counts = useMemo(
    () => ({
      new: results.filter((r) => r.kind === "new").length,
      update: results.filter((r) => r.kind === "update").length,
      unchanged: results.filter((r) => r.kind === "unchanged").length,
    }),
    [results],
  );
  const newRows = useMemo(
    () => results.filter((r) => r.kind === "new").sort((a, b) => a.row_number - b.row_number),
    [results],
  );
  const otherRows = useMemo(
    () => results.filter((r) => r.kind !== "new"),
    [results],
  );
  const newFieldColumns = useMemo(() => {
    const seen = new Set<string>();
    const columns: string[] = [];
    for (const row of newRows) {
      for (const field of row.incoming_fields) {
        if (seen.has(field.field)) continue;
        seen.add(field.field);
        columns.push(field.field);
      }
    }
    return columns;
  }, [newRows]);
  const valueFor = (row: CampaignContactScanResult, field: string) =>
    row.incoming_fields.find((f) => f.field === field)?.incoming_value ?? null;

  if (!open) return null;

  const toggleRow = (rowNumber: number) => {
    setSelected((prev) =>
      prev.includes(rowNumber) ? prev.filter((r) => r !== rowNumber) : [...prev, rowNumber],
    );
  };

  const toggleAll = () => setSelected(allSelected ? [] : results.map((r) => r.row_number));
  const canConfirm = selected.length > 0;
  const importCount = selected.length;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/45"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-contact-scan-title"
        className="bg-white rounded-2xl shadow-2xl w-[calc(100vw-2rem)] max-w-[92rem] overflow-hidden ring-1 ring-slate-500/10"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-bridge-border/60 bg-slate-50">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-slate-200 text-slate-700">
            <GitCompareArrows className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 id="campaign-contact-scan-title" className="text-base font-bold text-slate-900">
              Review {results.length} contact{results.length === 1 ? "" : "s"} to import
            </h2>
            <p className="text-xs text-slate-600 mt-0.5">{summaryPhrase(counts)}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {counts.new > 0 && (
                <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", KIND_COPY.new.badgeClass)}>
                  {counts.new} new
                </span>
              )}
              {counts.update > 0 && (
                <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", KIND_COPY.update.badgeClass)}>
                  {counts.update} update
                </span>
              )}
              {counts.unchanged > 0 && (
                <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", KIND_COPY.unchanged.badgeClass)}>
                  {counts.unchanged} unchanged
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-b border-bridge-border/60 bg-white">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-800 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-bridge-border accent-bridge-accent"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = selected.length > 0 && !allSelected;
              }}
              onChange={toggleAll}
            />
            Select all
            <span className="font-normal text-bridge-muted">
              ({selected.length} of {results.length} selected)
            </span>
          </label>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4 space-y-4">
          {newRows.length > 0 && (
            <div className={cn("rounded-xl border overflow-hidden", KIND_COPY.new.border)}>
              <div className={cn("flex items-center gap-2.5 px-3 py-2 border-b", KIND_COPY.new.header, KIND_COPY.new.border)}>
                <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide", KIND_COPY.new.badgeClass)}>
                  New
                </span>
                <span className="text-sm font-medium text-slate-800">
                  {newRows.length} contact{newRows.length === 1 ? "" : "s"} will be created
                </span>
              </div>
              {/* A wide CSV brings a column per field, so the table is allowed to
                  exceed the modal and scroll sideways. The tick box and row number
                  stay pinned so a row stays identifiable at any scroll position. */}
              <div className="overflow-x-auto">
                <table className="w-max min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-bridge-muted bg-white">
                      <th className="sticky left-0 z-10 bg-white px-3 py-1.5 font-semibold w-10" />
                      <th className="sticky left-10 z-10 bg-white px-3 py-1.5 font-semibold whitespace-nowrap border-r border-bridge-border/50">
                        Row
                      </th>
                      {newFieldColumns.map((field) => (
                        <th key={field} className="px-3 py-1.5 font-semibold whitespace-nowrap">
                          {field}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-bridge-border/50">
                    {newRows.map((result) => (
                      <tr key={result.row_number} className="align-top">
                        <td className="sticky left-0 z-10 bg-white px-3 py-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-bridge-border accent-bridge-accent"
                            checked={selectedSet.has(result.row_number)}
                            onChange={() => toggleRow(result.row_number)}
                            aria-label={`Select row ${result.row_number}`}
                          />
                        </td>
                        <td className="sticky left-10 z-10 bg-white px-3 py-2 text-xs font-bold text-slate-700 whitespace-nowrap border-r border-bridge-border/50">
                          {result.row_number}
                        </td>
                        {newFieldColumns.map((field) => (
                          <td
                            key={field}
                            className="px-3 py-2 text-emerald-900 font-medium whitespace-nowrap"
                          >
                            {shown(valueFor(result, field))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {otherRows.map((result) => {
            const kind = KIND_COPY[result.kind];
            return (
              <div
                key={result.row_number}
                className={cn("rounded-xl border overflow-hidden", kind.border)}
              >
                <label
                  className={cn(
                    "flex items-center gap-2.5 px-3 py-2 border-b cursor-pointer",
                    kind.header,
                    kind.border,
                  )}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-bridge-border accent-bridge-accent"
                    checked={selectedSet.has(result.row_number)}
                    onChange={() => toggleRow(result.row_number)}
                  />
                  <span className="text-xs font-bold text-slate-700">Row {result.row_number}</span>
                  <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide", kind.badgeClass)}>
                    {kind.badge}
                  </span>
                  {result.display_name && (
                    <span className="text-sm font-medium text-slate-800 truncate">
                      {result.display_name}
                    </span>
                  )}
                  <span className="ml-auto shrink-0 text-[11px] font-medium text-bridge-muted">
                    {result.kind === "update"
                      ? `${result.differences.length} field${result.differences.length === 1 ? "" : "s"} differ${result.differences.length === 1 ? "s" : ""}`
                      : kind.hint}
                  </span>
                </label>

                {(result.branch || result.label) && (
                  <p className="px-3 py-1.5 text-[11px] text-bridge-muted border-b border-bridge-border/50">
                    {[result.branch, result.label].filter(Boolean).join(" · ")}
                  </p>
                )}

                {result.kind === "update" && result.differences.length > 0 && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-bridge-muted bg-white">
                        <th className="px-3 py-1.5 font-semibold">Field</th>
                        <th className="px-3 py-1.5 font-semibold">Current</th>
                        <th className="px-3 py-1.5 font-semibold">From CSV</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bridge-border/50">
                      {result.differences.map((diff, index) => (
                        <tr key={`${diff.field}-${index}`}>
                          <td className="px-3 py-1.5 font-medium text-slate-800 align-top">
                            {diff.field}
                          </td>
                          <td className="px-3 py-1.5 text-bridge-muted align-top break-words">
                            {shown(diff.existing_value)}
                          </td>
                          <td className="px-3 py-1.5 text-amber-900 font-medium align-top break-words">
                            {shown(diff.incoming_value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {result.kind === "unchanged" && (
                  <p className="px-3 py-2 text-sm text-slate-600">
                    No fields differ from the stored contact. Importing keeps the record as it is and
                    still adds it to this campaign.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-4 border-t border-bridge-border/60 bg-slate-50 flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            disabled={!canConfirm || submitting}
            onClick={() => onConfirm(selected)}
          >
            Import {importCount} row{importCount === 1 ? "" : "s"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
